import { Check, ChevronDown, ChevronRight } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import { Button } from '../Button/Button'
import s from './FunnelStep.module.css'

/** Ce que l'entonnoir dit d'une étape : son numéro, son état, son résumé une fois repliée. */
export interface FunnelStepState {
  number: number
  open: boolean
  summary: string
  onToggle: () => void
  /** Ouvre l'étape suivante ; absent sur la dernière. */
  onContinue: (() => void) | undefined
}

export interface FunnelStepProps extends FunnelStepState {
  title: string
  children: ReactNode
}

/**
 * L'étape d'un entonnoir en accordéon — « Nouvelle demande » (revue du 20/10/2026, Matteo) : une
 * étape ouverte à la fois, les autres repliées sur un résumé d'une ligne, « Continuer » au pied.
 * Toutes restent atteignables dans n'importe quel ordre.
 *
 * ⚠️ **Une étape repliée reste montée** : sa saisie demeure, seul son corps est masqué. Démontée,
 * elle rendrait au défaut une liste déroulante qu'on venait de choisir.
 */
export function FunnelStep({
  number,
  open,
  summary,
  onToggle,
  onContinue,
  title,
  children,
}: FunnelStepProps) {
  const bodyId = useId()
  const summaryId = useId()
  const done = !open && summary !== ''

  return (
    <section className={s.root} data-open={open}>
      {/* Le motif d'accordéon : le bouton dans un titre, nommé par le titre de l'étape. */}
      <h3 className={s.heading}>
        <button
          type="button"
          className={s.head}
          aria-describedby={summaryId}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <span className={s.number} data-done={done} aria-hidden>
            {done ? <Check size={13} /> : number}
          </span>
          <span className={s.title}>{title}</span>
          {/* Masqué du nom, lu en description : `aria-describedby` lit un nœud masqué. */}
          <span id={summaryId} className={s.summary} aria-hidden>
            {open ? '' : summary}
          </span>
          <ChevronDown size={16} className={s.chevron} aria-hidden />
        </button>
      </h3>
      <div id={bodyId} className={s.body} data-open={open}>
        {children}
        {open && onContinue !== undefined && (
          <div className={s.actions}>
            <Button tone="brand" size="sm" onClick={onContinue}>
              Continuer <ChevronRight size={14} aria-hidden />
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * L'état d'un entonnoir de `count` étapes : la première ouverte, une seule à la fois. Rend, pour
 * l'étape `index`, ce que `FunnelStep` attend.
 */
export function useFunnel(count: number): (index: number, summary: string) => FunnelStepState {
  // `-1` quand toutes sont repliées.
  const [openStep, setOpenStep] = useState(0)
  return (index, summary) => ({
    number: index + 1,
    open: openStep === index,
    summary,
    onToggle: () => setOpenStep(openStep === index ? -1 : index),
    onContinue: index < count - 1 ? () => setOpenStep(index + 1) : undefined,
  })
}
